import { ExcelData } from "./types";

// Use environment variable for API URL with fallback to localhost
let API_BASE_URL = (import.meta.env.VITE_API_URL || "http://localhost:8000").trim();

// Add ngrok bypass parameter if needed
if (API_BASE_URL.includes('ngrok-free.app') || API_BASE_URL.includes('ngrok.io')) {
  // Make sure there's no trailing slash before adding the parameter
  API_BASE_URL = API_BASE_URL.replace(/\/$/, '');
  
  // Append the ngrok-skip-browser-warning parameter to bypass the interstitial
  API_BASE_URL = API_BASE_URL.includes('?') 
    ? `${API_BASE_URL}&ngrok-skip-browser-warning=true` 
    : `${API_BASE_URL}?ngrok-skip-browser-warning=true`;
  console.log("Added ngrok bypass parameter to URL:", API_BASE_URL);
}

// Get credentials from environment variables
const API_USERNAME = import.meta.env.VITE_API_USERNAME || "test_user";
const API_PASSWORD = import.meta.env.VITE_API_PASSWORD || "test_pass";

// Create base headers with authentication
const getAuthHeaders = () => ({
  'Content-Type': 'application/json',
  'Authorization': 'Basic ' + btoa(`${API_USERNAME}:${API_PASSWORD}`),
  // This header tells ngrok to skip the warning page
  'ngrok-skip-browser-warning': 'true'
});

// Log the configuration on initialization
console.log('API Configuration:', {
  baseUrl: API_BASE_URL,
  username: API_USERNAME,
  // Don't log the actual password
  hasPassword: !!API_PASSWORD
});

// Store Vercel deployment URL if we're on Vercel
const VERCEL_URL = window.location.origin.includes('vercel.app') ? window.location.origin : null;

// Try to fetch the current ngrok URL from a configuration endpoint - for future use
export const updateApiUrl = async () => {
  // Only for debug purposes - remove in production
  if (VERCEL_URL) {
    console.log("Running on Vercel. Attempting to get current API URL from a known source...");
    
    // Store the original URL in case we need to revert
    const originalUrl = API_BASE_URL;
    
    try {
      // Try the current URL first
      const healthUrl = buildUrl('/health');
      const response = await fetch(healthUrl, {
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        console.log("Current API URL is working fine!");
        return true;
      }
    } catch (error) {
      console.warn("Current API URL is not working, trying alternative sources...");
    }
    
    // Optionally, try other known URLs where the backend might be running
    const possibleUrls = [
      "https://995d-2401-4900-1cb1-29f-4415-230c-4551-7952.ngrok-free.app",
      "https://8551-2401-4900-1cb1-29f-4415-230c-4551-7952.ngrok-free.app"
    ];
    
    // Try each URL to see if any works
    for (const url of possibleUrls) {
      try {
        console.log(`Trying alternative URL: ${url}`);
        // Set the URL temporarily to test it
        const tempUrl = url;
        
        // For testing, we need to create a temporary URL with the health endpoint
        let healthUrl = tempUrl;
        if (!healthUrl.endsWith('/')) healthUrl += '/';
        healthUrl += 'health';
        
        const response = await fetch(healthUrl, {
          headers: getAuthHeaders()
        });
        
        if (response.ok) {
          console.log(`Found working API URL: ${url}`);
          // Update the global URL variable
          API_BASE_URL = url;
          console.log('Updated API configuration:', {
            baseUrl: API_BASE_URL,
            username: API_USERNAME,
            hasPassword: !!API_PASSWORD
          });
          return true;
        }
      } catch (error) {
        console.warn(`Alternative URL ${url} is not working either.`);
      }
    }
    
    console.error("Could not find a working API URL. Using the original one.");
    return false;
  }
  return true;
};

// Try to update the API URL on startup
updateApiUrl();

export interface ChatResponse {
  answer: string;
  matched_rows: any[];
  matched_count: number;
  match_scores: number[];
  strict_mode: boolean;
  processing_time: string;
}

export interface UploadResponse {
  status: 'processing' | 'completed';
  message: string;
  total_rows?: number;
  sheets?: string[];
  processing_time?: string;
}

export interface Collection {
  name: string;
  count: number;
}

export interface CollectionsResponse {
  collections: Collection[];
}

export interface QueryFilters {
  max_mop?: number;
  type?: string;
  has_cashback?: boolean;
  has_idfc_12m_0dbd?: boolean;
}

// Helper function to wait between retries
const wait = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

// Helper function to build proper endpoint URLs
const buildUrl = (endpoint: string): string => {
  // Make sure endpoint starts with a slash if it doesn't already
  if (!endpoint.startsWith('/')) {
    endpoint = '/' + endpoint;
  }
  
  // If the API URL already has query parameters
  if (API_BASE_URL.includes('?')) {
    // Get the base part before the query string
    const baseUrlPart = API_BASE_URL.split('?')[0];
    // Get the query string part
    const queryPart = API_BASE_URL.split('?')[1];
    // Combine them with the endpoint in the middle
    return `${baseUrlPart}${endpoint}?${queryPart}`;
  }
  
  // If no query parameters, just append the endpoint
  return `${API_BASE_URL}${endpoint}`;
};

// Test connection to backend
export const testConnection = async (): Promise<{ status: string; message: string }> => {
  try {
    console.log("Testing connection to backend...");
    const healthUrl = buildUrl('/health');
    console.log("Health check URL:", healthUrl);
    
    const response = await fetch(healthUrl, {
      headers: getAuthHeaders()
    });
    
    console.log("Test response status:", response.status);
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error("Test failed:", errorText);
      throw new Error(`Connection test failed: ${errorText}`);
    }
    
    const data = await response.json();
    console.log("Test response:", data);
    return data;
  } catch (error) {
    console.error("Connection test error:", error);
    throw error;
  }
};

// Collection Management Functions
export const listCollections = async (): Promise<CollectionsResponse> => {
  try {
    console.log("Attempting to list collections...");
    const collectionsUrl = buildUrl('/collections');
    console.log("Collections URL:", collectionsUrl);
    
    const response = await fetch(collectionsUrl, {
      headers: getAuthHeaders()
    });
    
    console.log("Response status:", response.status);
    console.log("Response headers:", Object.fromEntries(response.headers.entries()));
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error("Error response:", errorText);
      throw new Error(`Failed to list collections: ${errorText}`);
    }
    
    // Check content type
    const contentType = response.headers.get('content-type');
    console.log("Content-Type:", contentType);
    
    if (contentType && contentType.includes('text/html')) {
      console.error("Received HTML instead of JSON - likely hitting ngrok interstitial");
      const htmlText = await response.text();
      
      if (htmlText.includes('ngrok')) {
        console.error("Detected ngrok interstitial page. Refresh the page to try again with the bypass parameters.");
        throw new Error("Ngrok interstitial detected. Please refresh the page to bypass it.");
      } else {
        console.error("HTML response:", htmlText.substring(0, 200) + "...");
        throw new Error("Received HTML instead of JSON. The server might be returning an error page.");
      }
    }
    
    // Try to parse as JSON directly
    try {
      const data = await response.json();
      console.log("Collections data:", data);
      return data;
    } catch (jsonError) {
      console.error("JSON parsing error:", jsonError);
      
      // Try to get the raw text
      const responseClone = response.clone();
      const responseText = await responseClone.text();
      
      // Check for ngrok interstitial again
      if (responseText.includes('ngrok')) {
        console.error("Detected ngrok interstitial in response text.");
        throw new Error("Ngrok interstitial detected. Please refresh the page to bypass it.");
      }
      
      console.error("Response that failed to parse:", responseText);
      throw new Error(`Failed to parse response as JSON: ${jsonError.message}`);
    }
  } catch (error) {
    console.error("Error listing collections:", error);
    throw error;
  }
};

export const createCollection = async (name: string): Promise<{ message: string }> => {
  try {
    console.log("Attempting to create collection:", name);
    const collectionsUrl = buildUrl('/collections');
    
    const response = await fetch(collectionsUrl, {
      method: "POST",
      headers: getAuthHeaders(),
      body: JSON.stringify({ name }),
    });
    
    console.log("Response status:", response.status);
    if (!response.ok) {
      const errorText = await response.text();
      console.error("Error response:", errorText);
      throw new Error(`Failed to create collection: ${errorText}`);
    }
    
    const data = await response.json();
    console.log("Create collection response:", data);
    return data;
  } catch (error) {
    console.error("Error creating collection:", error);
    throw error;
  }
};

export const selectCollection = async (name: string): Promise<{ message: string; count: number }> => {
  try {
    console.log("Attempting to select collection:", name);
    const selectUrl = buildUrl('/collections/select');
    
    const response = await fetch(selectUrl, {
      method: "POST",
      headers: getAuthHeaders(),
      body: JSON.stringify({ name }),
    });
    
    console.log("Response status:", response.status);
    if (!response.ok) {
      const errorText = await response.text();
      console.error("Error response:", errorText);
      throw new Error(`Failed to select collection: ${errorText}`);
    }
    
    const data = await response.json();
    console.log("Select collection response:", data);
    return data;
  } catch (error) {
    console.error("Error selecting collection:", error);
    throw error;
  }
};

export const deleteCollection = async (name: string): Promise<{ message: string }> => {
  try {
    const deleteUrl = buildUrl(`/collections/${name}`);
    
    const response = await fetch(deleteUrl, {
      method: "DELETE",
      headers: getAuthHeaders()
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to delete collection: ${errorText}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error("Error deleting collection:", error);
    throw error;
  }
};

// Check if backend is available with retries
const checkBackendHealth = async (retries = 3): Promise<boolean> => {
  for (let i = 0; i < retries; i++) {
    try {
      console.log(`Health check attempt ${i + 1}/${retries}`);
      const healthUrl = buildUrl('/health');
      
      const response = await fetch(healthUrl, {
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        console.log("Backend health check successful");
        return true;
      }
    } catch (error) {
      console.error(`Health check attempt ${i + 1} failed:`, error);
      if (i < retries - 1) {
        console.log(`Waiting before next attempt...`);
        await wait(1000); // Wait 1 second between retries
      }
    }
  }
  return false;
};

export const uploadExcelFile = async (file: File): Promise<UploadResponse> => {
  console.log("Starting file upload...", file.name);
  
  // Check backend health first
  const isHealthy = await checkBackendHealth();
  if (!isHealthy) {
    throw new Error("Backend server is not available. Please make sure it's running on " + API_BASE_URL);
  }

  const formData = new FormData();
  formData.append("file", file);

  try {
    console.log("Sending request to backend...");
    const uploadUrl = buildUrl('/upload');
    
    const response = await fetch(uploadUrl, {
      method: "POST",
      headers: {
        'Authorization': 'Basic ' + btoa(`${API_USERNAME}:${API_PASSWORD}`)
      },
      body: formData,
    });

    console.log("Response status:", response.status);
    console.log("Response headers:", Object.fromEntries(response.headers.entries()));
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error("Upload error response:", errorText);
      let errorDetail;
      try {
        const errorJson = JSON.parse(errorText);
        errorDetail = errorJson.detail;
      } catch {
        errorDetail = errorText;
      }
      throw new Error(errorDetail || "Failed to upload file");
    }

    const data = await response.json();
    console.log("Upload response:", data);
    
    if (data.status === 'processing') {
      console.log("File processing started:", data.message);
      console.log("Total rows to process:", data.total_rows);
      console.log("Sheets to process:", data.sheets);
    } else if (data.status === 'completed') {
      console.log("Processing completed:", data.message);
      console.log("Total rows processed:", data.total_rows);
      console.log("Sheets processed:", data.sheets);
      console.log("Processing time:", data.processing_time);
    }
    
    return data;
  } catch (error) {
    console.error("Upload failed:", error);
    if (error instanceof Error) {
      throw error;
    }
    throw new Error("Failed to upload file");
  }
};

export const askQuestion = async (
  query: string,
  filters?: QueryFilters,
  strictMode: boolean = true
): Promise<ChatResponse> => {
  console.log("Sending question:", query);
  console.log("With filters:", filters);
  try {
    const askUrl = buildUrl('/ask');
    
    const response = await fetch(askUrl, {
      method: "POST",
      headers: getAuthHeaders(),
      body: JSON.stringify({
        query,
        filters,
        strict_mode: strictMode
      }),
    });

    console.log("Question response status:", response.status);
    
    if (!response.ok) {
      const error = await response.json();
      console.error("Question error:", error);
      throw new Error(error.detail || "Failed to get answer");
    }

    const data = await response.json();
    console.log("Question answered:", data);
    return data;
  } catch (error) {
    console.error("Question failed:", error);
    throw error;
  }
}; 